import React from 'react';
import { 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaSpinner, 
  FaTimes, 
  FaSave,
  FaClock,
  FaCalendarDay,
  FaUsers
} from 'react-icons/fa';

const PreviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  data,
  isLoading = false,
  title = '📋 Preview Timetable Data'
}) => {
  if (!isOpen || !data) return null;

  const { data: classData, summary, errors, warnings } = data;

  // ============================================================
  // Helper: Convert 24-hour to 12-hour format
  // ============================================================
  const to12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // ============================================================
  // Group entries by day for each class
  // ============================================================
  const groupByDay = (entries) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const grouped = {};
    days.forEach(day => { grouped[day] = []; });
    
    entries?.forEach(entry => {
      if (entry.dayOfWeek && grouped[entry.dayOfWeek]) {
        grouped[entry.dayOfWeek].push(entry);
      }
    });
    
    // Remove empty days
    Object.keys(grouped).forEach(day => {
      if (grouped[day].length === 0) delete grouped[day];
    });
    
    return grouped;
  };

  // ============================================================
  // Sort entries by time
  // ============================================================
  const sortByTime = (entries) => {
    return [...entries].sort((a, b) => {
      const timeA = a.timeSlot?.start || '00:00';
      const timeB = b.timeSlot?.start || '00:00';
      return timeA.localeCompare(timeB);
    });
  };

  // ============================================================
  // Get type color
  // ============================================================
  const getTypeColor = (type) => {
    const colors = {
      'Period': 'bg-blue-50/70 text-blue-700 border-blue-100 hover:bg-blue-50',
      'Lab': 'bg-purple-50/70 text-purple-700 border-purple-100 hover:bg-purple-50',
      'Break': 'bg-amber-50/70 text-amber-700 border-amber-100 hover:bg-amber-50',
      'Custom': 'bg-emerald-50/70 text-emerald-700 border-emerald-100 hover:bg-emerald-50',
      'Elective/Language Group': 'bg-pink-50/70 text-pink-700 border-pink-100 hover:bg-pink-50'
    };
    return colors[type] || 'bg-gray-50/70 text-gray-700 border-gray-100 hover:bg-gray-50';
  };

  // ============================================================
  // Get type icon
  // ============================================================
  const getTypeIcon = (type) => {
    const icons = {
      'Period': '📖',
      'Lab': '🧪',
      'Break': '☕',
      'Custom': '⚙️',
      'Elective/Language Group': '📋'
    };
    return icons[type] || '📌';
  };

  // ============================================================
  // Get type short label
  // ============================================================
  const getTypeShort = (type) => {
    const map = {
      'Period': 'Period',
      'Lab': 'Lab',
      'Break': 'Break',
      'Custom': 'Custom',
      'Elective/Language Group': 'Group'
    };
    return map[type] || type;
  };

  if (!isOpen || !data) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-6xl h-[92vh] sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden border border-slate-100 animate-in fade-in slide-in-from-bottom duration-200">
        
        {/* ============================================================
            HEADER AREA
            ============================================================ */}
        <div className="flex justify-between items-start p-4 sm:p-5 border-b border-slate-100 bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">{title}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap text-xs sm:text-sm text-slate-500 font-medium">
              <span className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100">
                <FaUsers className="text-indigo-500" />
                {classData?.length || 0} Class{classData?.length !== 1 ? 'es' : ''}
              </span>
              <span className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100">
                <FaClock className="text-indigo-500" />
                {summary?.totalRows || 0} Sessions
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-2 rounded-xl transition-all border border-transparent hover:border-slate-100"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* ============================================================
            MAIN SCROLLABLE CONTENT
            ============================================================ */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-6 bg-slate-50/50">
          {classData?.map((cls, classIdx) => {
            const groupedDays = groupByDay(cls.entries);
            const dayKeys = Object.keys(groupedDays);
            
            return (
              <div key={classIdx} className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm">
                {/* Class Title Badge */}
                <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-1.5 h-5 bg-indigo-600 rounded-full"></div>
                    <h3 className="text-base sm:text-emerald-950 font-bold text-slate-800">
                      {cls.className || `Class ${classIdx + 1}`}
                    </h3>
                  </div>
                  <span className="text-xs font-semibold bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded-full border border-indigo-100/50">
                    {cls.entries?.length || 0} slots
                  </span>
                </div>

                {/* Day Grid Layout */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {dayKeys.map((day) => {
                    const dayEntries = sortByTime(groupedDays[day]);
                    return (
                      <div key={day} className="border border-slate-200/60 rounded-xl bg-slate-50/30 overflow-hidden flex flex-col">
                        {/* Day Title */}
                        <div className="bg-slate-50/80 px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FaCalendarDay className="text-indigo-500 text-xs" />
                            <span className="font-bold text-xs sm:text-sm text-slate-700">
                              {day}
                            </span>
                          </div>
                          <span className="text-[11px] font-medium text-slate-400 bg-white px-1.5 py-0.5 rounded-md border border-slate-100">
                            {dayEntries.length}
                          </span>
                        </div>
                        
                        {/* Entries List inside Day */}
                        <div className="p-2 space-y-2 flex-1">
                          {dayEntries.map((entry, entryIdx) => (
                            <div 
                              key={entryIdx} 
                              className={`text-xs rounded-lg p-2.5 border transition-all ${getTypeColor(entry.type)}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="space-y-1 min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-mono font-bold text-slate-700 text-[11px]">
                                      {to12Hour(entry.timeSlot?.start)} - {to12Hour(entry.timeSlot?.end)}
                                    </span>
                                    <span className="text-[9px] px-1 font-semibold uppercase tracking-wider rounded bg-white/80 border border-current/10">
                                      {getTypeShort(entry.type)}
                                    </span>
                                  </div>
                                  
                                  <div className="font-semibold text-slate-800 truncate text-[13px]">
                                    {entry.subjectName || 
                                     entry.electiveGroupName || 
                                     entry.breakName || 
                                     entry.customName || 
                                     '—'}
                                  </div>

                                  {/* Info details row */}
                                  {(entry.room || entry.batchName) && (
                                    <div className="flex items-center gap-2 flex-wrap text-[11px] pt-0.5">
                                      {entry.room && (
                                        <span className="text-slate-500 font-medium truncate">
                                          📍 {entry.room}
                                        </span>
                                      )}
                                      {entry.batchName && (
                                        <span className="text-purple-600 font-medium bg-purple-50/80 px-1 rounded truncate">
                                          📦 {entry.batchName}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                <span className="text-base select-none p-0.5 bg-white/40 rounded-md">
                                  {getTypeIcon(entry.type)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Empty State for Class */}
                {dayKeys.length === 0 && (
                  <div className="text-center py-6 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                    No sessions scheduled for this class
                  </div>
                )}
              </div>
            );
          })}

          {/* Fully Empty State */}
          {(!classData || classData.length === 0) && (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200 max-w-md mx-auto p-6">
              <p className="text-3xl mb-2">📭</p>
              <h4 className="text-base font-bold text-slate-800">No data to preview</h4>
              <p className="text-xs text-slate-500 mt-1">Please upload a valid Excel file first to see the data here.</p>
            </div>
          )}
        </div>

        {/* ============================================================
            VALIDATION CHECKS & ALERTS PANEL
            ============================================================ */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 sticky bottom-[68px] sm:bottom-0 z-10 max-h-[25vh] overflow-y-auto">
          {/* Numerical Stats Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs sm:text-sm">
            <div className="bg-white rounded-lg p-2 border border-slate-200 text-center shadow-sm">
              <span className="text-slate-500 text-[11px] font-medium block">Total Rows</span>
              <p className="font-bold text-slate-800 text-base">{summary?.totalRows || 0}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-emerald-100 text-center shadow-sm">
              <span className="text-emerald-600 text-[11px] font-medium block">✅ Valid</span>
              <p className="font-bold text-emerald-700 text-base">{summary?.validRows || 0}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-rose-100 text-center shadow-sm">
              <span className="text-rose-600 text-[11px] font-medium block">❌ Errors</span>
              <p className="font-bold text-rose-700 text-base">{summary?.errorRows || 0}</p>
            </div>
            <div className="bg-white rounded-lg p-2 border border-amber-100 text-center shadow-sm">
              <span className="text-amber-600 text-[11px] font-medium block">⚠️ Warnings</span>
              <p className="font-bold text-amber-700 text-base">{summary?.warningRows || 0}</p>
            </div>
          </div>

          {/* Display active list errors */}
          {errors && errors.length > 0 && (
            <div className="mt-3 p-3 bg-rose-50 border border-rose-100 rounded-xl text-xs text-rose-800 shadow-sm">
              <p className="font-bold mb-1.5 flex items-center gap-1.5 text-rose-700">
                <FaExclamationTriangle className="text-rose-500" />
                Fix these errors before saving:
              </p>
              <ul className="list-disc list-inside space-y-1 pl-1 font-medium">
                {errors.slice(0, 5).map((err, i) => (
                  <li key={i} className="truncate">
                    Row {err.rowNumber}: {err.errors?.join(', ') || err.error || 'Invalid data setup'}
                  </li>
                ))}
                {errors.length > 5 && (
                  <li className="text-slate-500 list-none font-normal pt-1 pl-3">... and {errors.length - 5} more issues</li>
                )}
              </ul>
            </div>
          )}

          {/* Display active list warnings */}
          {warnings && warnings.length > 0 && (
            <div className="mt-2.5 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800 shadow-sm">
              <p className="font-bold mb-1.5 text-amber-700 flex items-center gap-1">⚠️ Please note these warnings:</p>
              <ul className="list-disc list-inside space-y-1 pl-1 font-medium">
                {warnings.slice(0, 3).map((warn, i) => (
                  <li key={i} className="truncate">
                    Row {warn.rowNumber}: {warn.warnings?.join(', ') || warn.warning || 'Check information details'}
                  </li>
                ))}
                {warnings.length > 3 && (
                  <li className="text-slate-500 list-none font-normal pt-1 pl-3">... and {warnings.length - 3} more minor items</li>
                )}
              </ul>
            </div>
          )}

          {/* Success Validation message */}
          {summary?.errorRows === 0 && summary?.validRows > 0 && (
            <div className="mt-2.5 p-2.5 bg-emerald-50 border border-emerald-100 rounded-xl text-xs text-emerald-800 font-semibold flex items-center gap-2 shadow-sm">
              <FaCheckCircle className="text-emerald-500 text-sm flex-shrink-0" />
              <span>Great! All {summary.validRows} rows verified cleanly and successfully.</span>
            </div>
          )}
        </div>

        {/* ============================================================
            BOTTOM ACTIONS FOOTER BAR
            ============================================================ */}
        <div className="flex items-center gap-3 p-3 sm:p-4 border-t border-slate-100 bg-white sticky bottom-0 z-20 justify-end w-full shadow-[0_-4px_12px_rgba(0,0,0,0.03)] sm:shadow-none">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 sm:flex-none px-4 py-2.5 text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-all text-xs sm:text-sm font-bold disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading || (errors && errors.length > 0)}
            className={`flex-[2] sm:flex-none px-5 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 text-xs sm:text-sm font-bold ${
              isLoading || (errors && errors.length > 0)
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 shadow-md active:shadow-sm'
            }`}
          >
            {isLoading ? (
              <>
                <FaSpinner className="animate-spin text-sm" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FaSave className="text-sm" />
                <span>Save All ({classData?.length || 0} classes)</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default PreviewModal;